import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Face to Face — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMOTION_EMOJI, EMOTION_LABEL } from './params';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'face_to_face.demo.step1_title',
        hintKey: 'face_to_face.demo.step1_hint',
        target: 'joie',
        candidates: ['joie', 'tristesse', 'colere'],
        durationMs: 1500,
    },
    {
        idx: 2,
        titleKey: 'face_to_face.demo.step2_title',
        hintKey: 'face_to_face.demo.step2_hint',
        target: 'peur',
        candidates: ['joie', 'colere', 'peur', 'surprise'],
        durationMs: 1200,
    },
    {
        idx: 3,
        titleKey: 'face_to_face.demo.step3_title',
        hintKey: 'face_to_face.demo.step3_hint',
        target: 'degout',
        candidates: ['tristesse', 'colere', 'degout', 'peur'],
        durationMs: 800,
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [pick, setPick] = useState(null);
    const timerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pick === step.target;
    useEffect(() => {
        setPhase('showing');
        setPick(null);
        timerRef.current = setTimeout(() => setPhase('awaiting'), step.durationMs);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [stepIdx, step.durationMs]);
    const handlePick = (e) => {
        if (phase !== 'awaiting')
            return;
        setPick(e);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('face_to_face.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex h-[200px] w-full max-w-[260px] items-center justify-center border border-cyan-border-faint bg-cyan-surface", children: [phase === 'showing' && (_jsx("span", { className: "text-[100px]", "aria-label": t('face_to_face.emotion_aria', { name: step.target }), children: EMOTION_EMOJI[step.target] })), phase === 'awaiting' && (_jsx("span", { className: "font-display text-[16px] text-cyan-text", children: t('face_to_face.question') })), phase === 'feedback' && (_jsxs("div", { className: "flex flex-col items-center gap-s2", children: [_jsx("span", { className: [
                                    'font-display text-[28px] font-medium',
                                    correct ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: correct ? '✓' : '✗' }), _jsx("span", { className: "text-[12px] text-cyan-text", children: t('face_to_face.answer_label', { emotion: EMOTION_LABEL[step.target] }) })] }))] }), phase === 'awaiting' && (_jsx("div", { className: "grid grid-cols-2 gap-s2", children: step.candidates.map((e) => (_jsxs("button", { type: "button", onClick: () => handlePick(e), className: "flex items-center gap-s2 border border-cyan-border bg-cyan-surface px-s3 py-s2 hover:bg-cyan-border-faint", children: [_jsx("span", { className: "text-[24px]", children: EMOTION_EMOJI[e] }), _jsx("span", { className: "font-display text-[13px] text-cyan-text", children: EMOTION_LABEL[e] })] }, e))) })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map